import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Callout, ErrorState } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CopyButton, Switch } from "@/components/ui/misc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogForm,
  DialogBody,
  DialogFooter,
  DiscardGuard,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useKeys, useTenants } from "@/lib/queries";
import {
  useSlackConnections,
  useSaveSlackConnection,
  useDeleteSlackConnection,
  type SlackConnection,
} from "@/lib/slack";

export function SlackPage() {
  const connections = useSlackConnections();
  const save = useSaveSlackConnection();
  const remove = useDeleteSlackConnection();
  const keys = useKeys();
  const { user } = useAuth();
  const tenants = useTenants({ enabled: user?.role === "superadmin" });
  const manifest = useQuery({
    queryKey: keys.scoped(["slack", "manifest"]),
    queryFn: () => api.get("/integrations/slack/manifest"),
  });
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SlackConnection>();
  const [name, setName] = React.useState("");
  const [token, setToken] = React.useState("");
  const [tenant, setTenant] = React.useState("");
  const [enabled, setEnabled] = React.useState(true);
  const [error, setError] = React.useState("");
  const [deleting, setDeleting] = React.useState<SlackConnection>();
  const start = (row?: SlackConnection) => {
    setEditing(row);
    setName(row?.name ?? "");
    setToken("");
    setTenant("");
    setEnabled(row?.enabled ?? true);
    setError("");
    setOpen(true);
  };
  const close = (next: boolean) => {
    setOpen(next);
    if (!next) setToken("");
  };
  const manifestText = JSON.stringify(manifest.data, null, 2);
  return (
    <Page>
      <PageHeader
        title="Slack"
        description="Connect a workspace, then choose channels and events in Webhooks."
        action={<Button onClick={() => start()}>Connect workspace</Button>}
      />
      <Card className="mb-4 space-y-3 p-4">
        <p className="text-sm text-text">
          Create a Slack app from this manifest, install it in your workspace, then paste its Bot User OAuth
          Token. Invite the app to private channels before choosing them.
        </p>
        <a
          className="text-sm text-text underline"
          href="https://api.slack.com/apps"
          target="_blank"
          rel="noreferrer"
        >
          Create a Slack app
        </a>
        {manifest.error && <ErrorState error={manifest.error} />}
        {manifest.data !== undefined && (
          <details>
            <summary className="cursor-pointer text-sm">App manifest</summary>
            <CopyButton value={manifestText} label="Copy manifest" />
            <pre className="overflow-auto bg-surface-sunken p-3 text-xs text-text">{manifestText}</pre>
          </details>
        )}
      </Card>
      {connections.error && <ErrorState error={connections.error} />}
      {remove.error && <Callout tone="danger">{remove.error.message}</Callout>}
      {connections.isPending && <p className="text-sm text-text-muted">Loading workspaces…</p>}
      {connections.data?.length === 0 && (
        <p className="text-sm text-text-muted">No workspace connected yet.</p>
      )}
      <div className="space-y-3">
        {connections.data?.map((row) => (
          <Card key={row.id} className="space-y-2 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-text">{row.name}</p>
                <p className="text-xs text-text-muted">
                  {row.team_name} ·{" "}
                  {!row.enabled ? "Disabled" : row.status === "ok" ? "Connected" : "Needs attention"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => start(row)}>
                  Manage
                </Button>
                <Button variant="ghost" onClick={() => setDeleting(row)}>
                  Delete
                </Button>
              </div>
            </div>
            {row.last_error && <Callout tone="warning">{row.last_error}</Callout>}
            <Link to="/webhooks" className="text-sm underline">
              Configure channels and events
            </Link>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent>
          <DiscardGuard
            changed={
              name !== (editing?.name ?? "") ||
              Boolean(token) ||
              enabled !== (editing?.enabled ?? true) ||
              Boolean(tenant)
            }
          />
          <DialogHeader>
            <DialogTitle>{editing ? "Manage workspace" : "Connect workspace"}</DialogTitle>
            <DialogDescription>
              The bot token is checked with Slack and is never shown again.
            </DialogDescription>
          </DialogHeader>
          <DialogForm
            onSubmit={async (e) => {
              e.preventDefault();
              setError("");
              try {
                await save.mutateAsync({
                  id: editing?.id,
                  name,
                  ...(token ? { token } : {}),
                  enabled,
                  ...(tenant ? { tenant_id: Number(tenant) } : {}),
                });
                close(false);
              } catch (err) {
                setError(err instanceof Error ? err.message : "Could not save workspace");
              }
            }}
          >
            <DialogBody className="space-y-4">
              {error && <Callout tone="danger">{error}</Callout>}
              <Field label="Name" htmlFor="slack-name" required>
                <Input
                  id="slack-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={200}
                />
              </Field>
              {!editing && user?.role === "superadmin" && (tenants.data?.length ?? 0) > 1 && (
                <Field label="Tenant" htmlFor="slack-tenant" required>
                  <Select value={tenant} onValueChange={setTenant}>
                    <SelectTrigger id="slack-tenant">
                      <SelectValue placeholder="Choose tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.data?.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Field
                label={editing ? "Replace bot token" : "Bot token"}
                htmlFor="slack-token"
                hint={editing ? "Leave blank to keep the current token." : "Starts with xoxb-"}
              >
                <Input
                  id="slack-token"
                  type="password"
                  autoComplete="new-password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required={!editing}
                />
              </Field>
              {editing && (
                <div className="flex justify-between">
                  <span className="text-sm">Enabled</span>
                  <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Enable connection" />
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={save.isPending}>
                Save workspace
              </Button>
            </DialogFooter>
          </DialogForm>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => {
          if (!v) setDeleting(undefined);
        }}
        title="Delete workspace connection?"
        description="Remove its endpoints first. You can reconnect the workspace later."
        confirmLabel="Delete"
        onConfirm={async () => {
          if (deleting) {
            try {
              await remove.mutateAsync(deleting.id);
            } catch {
              /* Error is shown in the list. */
            }
            setDeleting(undefined);
          }
        }}
      />
    </Page>
  );
}
